import React, { useCallback, useMemo, useState } from "react";

import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import withRoleCheck from "components/hocs/withRoleCheck";
import { checkIsAuthorized, getNextQueryParams } from "components/helpers";
import EntityService from "services/Entity";
import ContactService from "services/Contact";
import { AddToCampaignModal as _AddToCampaignModal } from "components/modals/AddToCampaignModal";
import { useCampaignViewTable } from "hooks/useCampaignViewTable";

export const CAMPAIGN_TABLE_CONSTANTS = {
  headerIcon: null,
  header: null,
  tableName: "campaign",
  className: "Campaign",
  exportTableName: null,
};

const AddToCampaignModal = withRoleCheck(_AddToCampaignModal, [
  ACL_RELATIONSHIPS.campaignTarget.create,
]);

const CampaignTableV3 = ({
  entityId = null,
  contactId = null,
  sendEmail = false,
}) => {
  const [addedCampaigns, setAddedCampaigns] = useState([]);
  const canEmail = useMemo(() => {
    if (entityId) {
      return checkIsAuthorized([ACL_RELATIONSHIPS.entityEmail.create]);
    } else if (contactId) {
      return checkIsAuthorized([ACL_RELATIONSHIPS.contactEmail.create]);
    }
    return false;
  }, [entityId, contactId]);

  const { columns, queryKey, invalidateData } = useCampaignViewTable({
    canEmail,
    sendEmail,
    entityId,
    contactId,
  });

  const fetchCampaigns = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        let response;
        if (entityId) {
          response = await EntityService.getCampaignTable(
            entityId,
            nextQueryParams
          );
        } else if (contactId) {
          response = await ContactService.getCampaignTable(
            contactId,
            nextQueryParams
          );
        } else {
          throw new Error(
            "One of props 'entityId' or 'contactId' was not specified in 'CampaignTableV3'."
          );
        }
        if (response?.data) {
          setAddedCampaigns(
            response.data.map(campaign => campaign.campaign_id)
          );
        }
        return response;
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        throw error;
      }
    },
    [columns, contactId, entityId]
  );

  return (
    <BaseTableV3
      columns={columns}
      tableName={CAMPAIGN_TABLE_CONSTANTS.tableName}
      queryKey={queryKey}
      className={CAMPAIGN_TABLE_CONSTANTS.className}
      createButton={
        <AddToCampaignModal
          entityId={entityId}
          contactId={contactId}
          campaignsAdded={addedCampaigns}
          onSuccess={invalidateData}
        />
      }
      fetchData={fetchCampaigns}
      invalidateData={invalidateData}
      enableSettings
    />
  );
};

export default CampaignTableV3;
